/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, VERSION } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { assetsConst } from '../../const/assets.const';
import { SideBarConst } from '../../const/sideBarConst';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SideBar } from 'src/app/models/sideBar';
import { getPermissions } from '../../helpers/getPermissions.helper';
import { getSidebarPermissions } from '../../helpers/getSidebarPermissions.helper';
import { ModuleConst } from '../../const/moduleConst';
import { AccessTypes } from '../../const/accessRole';
import { TooltipPosition } from '@angular/material/tooltip';

@Component({
  selector: 'mega-store-factory-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  // The collapsed rail sits on the right in Arabic, so its tooltips have to
  // open towards the content instead of off the edge of the screen.
  tooltipPosition: TooltipPosition =
    document.documentElement.dir === 'rtl' ? 'left' : 'right';
  @Output() urlOutput = new EventEmitter<any>();
  assetsConst = assetsConst;
  sideBar: SideBar[] = [];
  filteredSideBar: SideBar[] = [];
  public innerWidth: any;
  panelOpenState = false;
  version = VERSION;
  sidenavWidth = 15;
  ngStyle!: string;
  private destroy$ = new Subject<void>();
  adminRole = 5;
  subAdminRole = 9;
  permissions: any = [];
  currentProfileData: any = null;

  constructor(
    public _authService: AuthService,
    public translateService: TranslateService) {
    this.sideBar = SideBarConst;
  }

  ngOnInit(): void {
    this.subscribeToProfileDetails();
  }

  subscribeToProfileDetails(): void {
    this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileData => {
        if (profileData) {
          this.currentProfileData = profileData;
          this.filterSidebarItems(profileData);
        }
      });
  }

  filterSidebarItems(profileData: any): void {
    this.permissions = getPermissions(profileData);

    if (profileData.role === this.adminRole) {
      this.filteredSideBar = this.sideBar;
      return;
    }

    if (profileData.role === this.subAdminRole) {
      this.filteredSideBar = this.sideBar
        .map(item => {
          if (item.sideBarId === ModuleConst.SubAdmin) {
            return null;
          }

          const mainItemPermission = this.permissions.find((permission: any) => 
            permission.sideBarId === item.sideBarId && permission.isView
          );

          if (item.isSubMenu && item.subMenuItems && item.subMenuItems.length > 0) {
            const filteredSubItems = item.subMenuItems.filter(subItem => {
              const subPermission = this.permissions.find((permission: any) => 
                permission.sideBarId === subItem.sideBarId && permission.isView
              );
              return subPermission !== undefined;
            });

            if (mainItemPermission || filteredSubItems.length > 0) {
              return {
                ...item,
                subMenuItems: filteredSubItems
              };
            }
            return null;
          }

          if (mainItemPermission) {
            return item;
          }

          return null;
        })
        .filter(item => item !== null) as Array<SideBar>;
      return;
    }

    this.filteredSideBar = this.sideBar;
  }

  increase(): void {
    this.sidenavWidth = 15;
  }
  
  decrease(): void {
    this.sidenavWidth = 4;
  }
  
  @HostListener('window', ['$event'])
  toggleSidenav(): void {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth <= 991) {
      this._authService.toggle();
    }
  }

  /**
   * Get sidebar permissions for a specific module/sideBarId
   * Can be used in HTML template to check permissions
   * @param sideBarId - The sideBarId from ModuleConst to check permissions for
   * @returns AccessTypes object with isView, isAdd, isEdit, isDelete permissions
   */
  getSidebarPermissions(sideBarId: number): AccessTypes {
    if (!this.currentProfileData) {
      return {
        isAdd: false,
        isDelete: false,
        isEdit: false,
        isView: false
      };
    }
    return getSidebarPermissions(this.currentProfileData, sideBarId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
